import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Minus, Plus, ShoppingBag, ExternalLink, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { isShopifyConfigured } from '@/lib/shopify';

const CartDrawer: React.FC = () => {
  const { state, updateQuantity, removeItem, closeCart } = useCart();

  const handleCheckout = () => {
    if (isShopifyConfigured() && state.checkoutUrl) {
      window.open(state.checkoutUrl, '_blank');
    } else {
      // Mock checkout for development
      alert('🛒 Mock Checkout\n\nIn production, this would redirect to Shopify checkout.\n\nTotal: $' + state.total.toFixed(2));
    }
  };

  const handleUpdateQuantity = async (lineItemId: string, currentQuantity: number, change: number) => {
    const newQuantity = Math.max(1, currentQuantity + change);
    if (newQuantity !== currentQuantity) {
      await updateQuantity(lineItemId, newQuantity);
    }
  };

  const handleRemoveItem = async (lineItemId: string) => {
    await removeItem(lineItemId);
  };

  return (
    <AnimatePresence>
      {state.isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={closeCart}
          />

          {/* Cart Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-gray-900 shadow-2xl z-50 border-l border-gray-700"
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700">
                <div className="flex items-center gap-3">
                  <ShoppingBag className="w-6 h-6 text-lime-400" />
                  <h2 className="text-xl font-bold text-white">
                    Your Cart ({state.itemCount})
                  </h2>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeCart}
                  className="text-gray-400 hover:text-white hover:bg-gray-800"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto p-6">
                {state.items.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingBag className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">Your cart is empty</h3>
                    <p className="text-gray-400 mb-6">Add some items to get started!</p>
                    <Button
                      onClick={closeCart}
                      className="bg-lime-400 hover:bg-lime-500 text-black font-semibold"
                    >
                      Continue Shopping
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {state.items.map((item) => (
                      <motion.div
                        key={item.id}
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -100 }}
                        transition={{ duration: 0.3 }}
                        className="bg-gray-800 rounded-lg p-4 border border-gray-700"
                      >
                        <div className="flex gap-4">
                          {/* Product Image */}
                          <div className="w-16 h-16 bg-gray-700 rounded-lg overflow-hidden flex-shrink-0">
                            {item.image ? (
                              <img
                                src={item.image}
                                alt={item.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-lime-400/20 flex items-center justify-center">
                                <span className="text-lime-400 text-xs font-bold">PBX</span>
                              </div>
                            )}
                          </div>

                          {/* Product Details */}
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white font-semibold text-sm mb-1 truncate">
                              {item.title}
                            </h4>
                            <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
                              {item.size && <span>Size: {item.size}</span>}
                              {item.size && item.color && <span>•</span>}
                              {item.color && <span>Color: {item.color}</span>}
                            </div>
                            <div className="text-lime-400 font-bold text-sm">
                              ${item.price.toFixed(2)}
                            </div>
                          </div>

                          {/* Remove Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveItem(item.id)}
                            className="text-gray-400 hover:text-red-400 hover:bg-red-500/10 p-2"
                            disabled={state.isLoading}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUpdateQuantity(item.id, item.quantity, -1)}
                              disabled={item.quantity <= 1 || state.isLoading}
                              className="w-8 h-8 p-0 border-gray-600 text-gray-400 hover:text-white hover:border-lime-400"
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            
                            <span className="w-8 text-center text-white font-semibold">
                              {item.quantity}
                            </span>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUpdateQuantity(item.id, item.quantity, 1)}
                              disabled={state.isLoading}
                              className="w-8 h-8 p-0 border-gray-600 text-gray-400 hover:text-white hover:border-lime-400"
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>

                          {/* Item Total */}
                          <div className="text-white font-semibold">
                            ${(item.price * item.quantity).toFixed(2)}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer with Total and Checkout */}
              {state.items.length > 0 && (
                <div className="border-t border-gray-700 p-6 space-y-4">
                  {/* Subtotal */}
                  <div className="flex justify-between items-center text-lg">
                    <span className="text-gray-300">Subtotal</span>
                    <span className="text-white font-bold">${state.subtotal.toFixed(2)}</span>
                  </div>

                  {/* Shipping Notice */}
                  <div className="text-xs text-gray-400 text-center">
                    {state.subtotal >= 75 ? (
                      <span className="text-lime-400">🚚 Free shipping included!</span>
                    ) : (
                      <span>Add ${(75 - state.subtotal).toFixed(2)} more for free shipping</span>
                    )}
                  </div>

                  {/* Checkout Button */}
                  <Button
                    onClick={handleCheckout}
                    disabled={state.isLoading}
                    className="w-full bg-lime-400 hover:bg-lime-500 text-black font-bold py-3 text-base transition-all duration-300 hover:scale-105"
                  >
                    {state.isLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        Checkout ${state.total.toFixed(2)}
                        <ExternalLink className="w-4 h-4" />
                      </div>
                    )}
                  </Button>

                  {/* Development Notice */}
                  {!isShopifyConfigured() && (
                    <div className="text-xs text-orange-400 text-center bg-orange-500/10 rounded p-2 border border-orange-500/20">
                      🧪 Demo Mode: This will show a mock checkout
                    </div>
                  )}

                  {/* Continue Shopping */}
                  <Button
                    variant="outline"
                    onClick={closeCart}
                    className="w-full border-gray-600 text-gray-300 hover:text-white hover:border-lime-400"
                  >
                    Continue Shopping
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer; 